/** Domain model: one ordered timeline spanning radar past → now → forecast hours. */

export const ISRAEL = {
  center: [34.8, 31.5],
  zoom: 7.2,
  bounds: [
    [34.15, 29.4],
    [35.95, 33.5],
  ],
};

/**
 * @typedef {'rain'|'temp'|'wind'} LayerKind
 * @typedef {'radar'|'forecast'} Phase
 * @typedef {{ t: number, kind: Phase, radarIndex?: number, forecastIndex?: number }} TimelineFrame
 */

export function createState() {
  return {
    lang: /** @type {'he'|'en'} */ (localStorage.getItem('rain-il-lang') || 'he'),
    layer: /** @type {LayerKind} */ ('rain'),
    playing: true,
    index: 0,
    userLoc: /** @type {null | {lat:number, lon:number}} */ (null),
    /** @type {import('./data/radar.js').RadarFrame[]} */
    radar: [],
    /** @type {import('./data/forecast.js').ForecastGrid | null} */
    forecast: null,
    /** @type {TimelineFrame[]} */
    timeline: [],
    nowUnix: Math.floor(Date.now() / 1000),
    glance: /** @type {null | {mode:string, whenLabel?:string, precipMm?:number}} */ (null),
    crossfade: 0,
    reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
}

/** Build scrubber frames: radar past, then forecast hours after now. */
export function buildTimeline(radar, forecast, nowUnix) {
  /** @type {import('./state.js').TimelineFrame[]} */
  const frames = [];
  radar.forEach((r, i) => {
    frames.push({ t: r.t, kind: 'radar', radarIndex: i });
  });
  if (forecast) {
    forecast.timesUnix.forEach((t, i) => {
      if (t >= nowUnix - 1800) {
        frames.push({ t, kind: 'forecast', forecastIndex: i });
      }
    });
  }
  if (!frames.length) {
    frames.push({ t: nowUnix, kind: 'radar', radarIndex: 0 });
  }
  return frames;
}

export function nearestIndex(timeline, unix) {
  let best = 0;
  let bestD = Infinity;
  timeline.forEach((f, i) => {
    const d = Math.abs(f.t - unix);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

export function nowMarkerRatio(timeline, nowUnix) {
  if (timeline.length < 2) return 0.5;
  const t0 = timeline[0].t;
  const t1 = timeline[timeline.length - 1].t;
  if (t1 === t0) return 0.5;
  return Math.min(1, Math.max(0, (nowUnix - t0) / (t1 - t0)));
}
